import { ChangeEvent } from '@faro/realtime-services';
import { Query, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Observable } from 'rxjs';

export interface InactiveQueryInvalidationOptions {
    /** Observable of change events from the module's change event service */
    events: Observable<ChangeEvent<any>[]>;
    /** Predicate to determine if a query should be invalidated based on change events */
    shouldInvalidate: (queryKey: readonly unknown[], changeEvents: ChangeEvent<any>[]) => boolean;
    /** Whether the hook is enabled. Defaults to true. */
    enabled?: boolean;
}

/**
 * Hook that invalidates inactive (unmounted) queries when change events occur.
 * Active queries are skipped because they have their own event listeners.
 *
 * This solves the stale data problem when navigating between views - mutations
 * emit change events, but unmounted components aren't listening. This hook
 * ensures those inactive queries are invalidated so they refetch when remounted.
 */
export function useInactiveQueryInvalidation(options: InactiveQueryInvalidationOptions): void {
    const { events, shouldInvalidate, enabled = true } = options;
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!enabled) {
            return;
        }
        const subscription = events.subscribe((changeEvents: ChangeEvent<any>[]) => {
            queryClient.invalidateQueries({
                predicate: (query: Query) => {
                    // Only invalidate inactive queries (no mounted observers)
                    if (query.getObserversCount() > 0) {
                        return false;
                    }
                    return shouldInvalidate(query.queryKey, changeEvents);
                },
            });
        });
        return () => subscription.unsubscribe();
    }, [events, shouldInvalidate, queryClient, enabled]);
}
